import {
  Component,
  EventEmitter,
  HostBinding,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import {
  AbstractFilterElementComponent,
  FilterEmitterType,
} from './abstract-filter-element.component';
import {
  FilterElementSize,
  gridClassesForFilterElementSize,
  ResultFieldOption,
} from './filter-elements';
import { FormsModule } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import { SelectModule } from 'primeng/select';
import { InputTextModule } from 'primeng/inputtext';

interface ResultFieldFilter {
  id: string;
  field: string | null;
  value: string;
}

let nextFilterId = 0;

function generateFilterId(): string {
  return `rf_${nextFilterId++}_${Date.now()}`;
}

@Component({
  selector: 'result-field-multi-filter-element',
  templateUrl: './result-field-multi-filter.component.html',
  imports: [
    FormsModule,
    TranslatePipe,
    SelectModule,
    InputTextModule,
  ],
  styles: `
    :host ::ng-deep .p-inputtext {
      padding: 6px 12px;
    }
  `,
})
export class ResultFieldMultiFilterElementComponent
  extends AbstractFilterElementComponent
  implements OnChanges
{
  @HostBinding('class')
  override componentClass = '';

  @Input()
  override size: FilterElementSize = FilterElementSize.LARGE;

  @Input()
  override label: string | undefined;

  @Input()
  override filterActive = false;

  @Input()
  override filterKey!: string;

  @Input()
  fields: ResultFieldOption[] = [];

  @Input()
  fieldPlaceholder = 'filter.result-field.select-field';

  @Input()
  valuePlaceholder = 'filter.result-field.enter-value';

  @Output()
  override filterTerm: EventEmitter<FilterEmitterType> = new EventEmitter();

  /**
   * Emits when result field filters are applied.
   * Format: array of { field: string, value: string } objects
   */
  @Output()
  resultFiltersChange: EventEmitter<{ field: string; value: string }[]> =
    new EventEmitter();

  filters: ResultFieldFilter[] = [
    { id: generateFilterId(), field: null, value: '' },
  ];

  ngOnChanges(changes: SimpleChanges) {
    if (changes['size']) {
      this.componentClass = gridClassesForFilterElementSize(
        changes['size'].currentValue,
      );
    }
  }

  override onFilterChange(_changes: unknown): void {
    this.applyFilters();
  }

  addFilter(): void {
    this.filters.push({ id: generateFilterId(), field: null, value: '' });
  }

  removeFilter(id: string): void {
    this.filters = this.filters.filter((f) => f.id !== id);
    if (this.filters.length === 0) {
      this.filters.push({ id: generateFilterId(), field: null, value: '' });
    }
    this.applyFilters();
  }

  onFieldChange(_filter: ResultFieldFilter): void {
    this.applyFilters();
  }

  onValueChange(_filter: ResultFieldFilter): void {
    this.applyFilters();
  }

  private applyFilters(): void {
    const activeFilters = this.filters
      .filter((f) => f.field && f.value.trim())
      .map((f) => ({
        field: f.field!,
        value: f.value.trim(),
      }));

    if (activeFilters.length > 0) {
      // Emit filter values for the data table
      const filterStrings = activeFilters.map(
        (f) => `${f.field}:contains:${f.value}`,
      );
      this.filterTerm.emit({
        key: this.filterKey,
        term: filterStrings.join('|'),
      });
      this.resultFiltersChange.emit(activeFilters);
    } else {
      this.filterTerm.emit({ key: this.filterKey, term: undefined });
      this.resultFiltersChange.emit([]);
    }
  }

  clearFilter(filter: ResultFieldFilter): void {
    filter.field = null;
    filter.value = '';
    this.applyFilters();
  }

  clearAllFilters(): void {
    this.filters = [{ id: generateFilterId(), field: null, value: '' }];
    this.filterTerm.emit({ key: this.filterKey, term: undefined });
    this.resultFiltersChange.emit([]);
  }

  trackByFilterId(_index: number, filter: ResultFieldFilter): string {
    return filter.id;
  }

  hasActiveFilters(): boolean {
    return this.filters.some((f) => f.field && f.value.trim());
  }
}
